// apps/web/src/app/shell/PenjagaPerubahan.tsx
/**
 * The app's only router blocker (react-router consults only the last blocker a page
 * registers, so no screen registers its own). While any form holds unsaved changes
 * (useJagaPerubahan), leaving by a link, Back, or a closing tab asks "Tinggalkan tanpa
 * menyimpan?" (Tinggalkan / Kembali ke isian); a question asked by a control (`useJaga.tanya`)
 * shows here too. A move that stays at the same address, the one right after a save or Batal,
 * and the move to the sign-in screens after a sign-out or a lock pass (jagaAturan); a move to
 * them while the session is open asks like any other. Tinggalkan lets
 * every registered form drop its changes first (a screen that outlives the move shows nothing
 * of them), then goes; Kembali ke isian stays, and the focus returns where it was. The app's
 * own reloads (`lepasPenjagaTab`) are never held.
 */
import { uiStrings } from '@satu/shared'
import { useEffect } from 'react'
import { useBlocker } from 'react-router'
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog'
import { jumlahKotor, useJaga } from '../store/jaga'
import { harusTanya, type Lokasi } from '../store/jagaAturan'
import { useSesi } from '../store/sesi'

const teks = uiStrings.isian.tinggalkan

/** Asked by the router at each navigation; a pass is used by the first one it lets through. */
function tahanPindah({
  currentLocation,
  nextLocation,
}: {
  currentLocation: Lokasi
  nextLocation: Lokasi
}): boolean {
  const s = useJaga.getState()
  const tanya = harusTanya(
    {
      jumlahAktif: jumlahKotor(s),
      lewatiSampai: s.lewatiSampai,
      sesiTerbuka: useSesi.getState().status === 'aktif',
    },
    Date.now(),
    currentLocation,
    nextLocation,
  )
  if (!tanya && s.lewatiSampai !== 0) s.pakaiLewati()
  return tanya
}

export function PenjagaPerubahan() {
  const blocker = useBlocker(tahanPindah)
  const terdaftar = useJaga(
    (s) => Object.keys(s.aktif).length > 0 || Object.keys(s.periksa).length > 0,
  )
  const permintaan = useJaga((s) => s.permintaan)
  const jawab = useJaga((s) => s.jawab)

  useEffect(() => {
    if (!terdaftar) return
    const tahan = (event: BeforeUnloadEvent) => {
      const s = useJaga.getState()
      if (s.tabDilepas || jumlahKotor(s) === 0) return
      event.preventDefault()
      // Older browsers (and some WebViews) ask only when returnValue is set.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', tahan)
    return () => window.removeEventListener('beforeunload', tahan)
  }, [terdaftar])

  const ditahan = blocker.state === 'blocked'
  return (
    <ConfirmDialog
      open={ditahan || permintaan !== null}
      onOpenChange={(open) => {
        if (open) return
        if (ditahan) blocker.reset()
        else jawab(false)
      }}
      judul={teks.judul}
      kalimat={teks.kalimat}
      ya={teks.ya}
      batal={teks.batal}
      onYa={() => {
        if (ditahan) {
          // The move goes on as "proceeding", which the router does not ask about again; a form
          // whose reset fails does not keep the person on the page.
          try {
            useJaga.getState().buangSemua()
          } finally {
            blocker.proceed()
          }
        } else {
          jawab(true)
        }
      }}
    />
  )
}
